// src/components/dashboard/DashboardInsights.jsx
import React, { useState, useMemo, useEffect } from "react";
import _ from "lodash";
import { useDetailedInsightsData } from "@/hooks/post-analysis/useDetailedInsightsData";
import { formatValue } from "@/lib/post_analysis_utils/formatValue";
import { fetchMetricOptions } from "@/services/api";
import { exportToCSV } from "@/lib/post_analysis_utils/exportToCSV";

import {
  POC_COLUMNS,
  REGION_COLUMNS,
  BRAND_COLUMNS,
  CAMPAIGN_COLUMNS,
} from "@/components/postanalysis components/tables/table_columns";

import Panel from "@/components/postanalysis components/ui/Panel";
import UnifiedTable from "@/components/postanalysis components/tables/UnifiedTable";
import { usePostAnalysisFilters } from "@/hooks/post-analysis/usePostAnalysisFilters";
import PostAnalysisHeaderWithFilters from "@/components/postanalysis components/ui/PostAnalysisHeaderWithFilters";

const SORT_OPTIONS = {
  "Highest First": (a, b) => b.value - a.value,
  "Lowest First": (a, b) => a.value - b.value,
  "Alphabetical": (a, b) => a.name.localeCompare(b.name)
};

// ==================== Main Component ====================
export default function DetailedInsights({ backendData = [], filters = { brand: "", region: "", campaign: "", start_date: "", end_date: "", poc_id: [], key_account: "" }, setFilters }) {
  const [showFilters, setShowFilters] = useState(false);
  const [metricOptions, setMetricOptions] = useState({ y_axis: [] });

  // Add local state for each panel's Y-Axis selection
  const [pocMetricAxis, setPocMetricAxis] = useState('impressions');
  const [regionMetricAxis, setRegionMetricAxis] = useState('impressions');
  const [brandMetricAxis, setBrandMetricAxis] = useState('impressions');
  const [campaignMetricAxis, setCampaignMetricAxis] = useState('impressions');

  // Fetch metric options from backend on mount
  useEffect(() => {
    async function fetchOptions() {
      const options = await fetchMetricOptions("detailed_insights");
      setMetricOptions(options);
    }
    fetchOptions();
  }, []);

  // Fetch data for each panel with the selected metric
  const pocData = useDetailedInsightsData(filters, pocMetricAxis);
  const regionData = useDetailedInsightsData(filters, regionMetricAxis);
  const brandData = useDetailedInsightsData(filters, brandMetricAxis);
  const campData = useDetailedInsightsData(filters, campaignMetricAxis);

  // Shared local filter options + change handler, seeded from pocData.filterOptions
  const { localFilterOptions, handleLocalFiltersChange } = usePostAnalysisFilters(
    pocData.filterOptions
  );

  // Helper function to get metric type
  const getMetricType = (metricKey) => {
    const metric = metricOptions.y_axis.find(opt => opt.value === metricKey);
    return metric?.type || 'number';
  };

  // Helper to get label from value
  const getLabelFromValue = (value) => metricOptions.y_axis.find(opt => opt.value === value)?.label || value;
  // Helper to get value from label
  const getValueFromLabel = (label) => metricOptions.y_axis.find(opt => opt.label === label)?.value || label;

  // Filter out revenue and volume uplift from metric options
  const filteredYAxisOptions = (metricOptions?.y_axis || []).filter(opt => opt.value !== 'revenue' && opt.value !== 'volume_uplift');

  // yAxisOptions for dropdowns
  const yAxisOptions = filteredYAxisOptions.map(opt => opt.label);

  // Export handlers for each table
  const handleExportPocCSV = () => {
    exportToCSV(pocData.pocLevel?.table_data || [], "poc_level_table.csv");
  };
  const handleExportCampaignCSV = () => {
    exportToCSV(campData.campaignLevel?.table_data || [], "campaign_level_table.csv");
  };
  const handleExportRegionCSV = () => {
    exportToCSV(regionData.regionLevel?.table_data || [], "region_level_table.csv");
  };
  const handleExportBrandCSV = () => {
    exportToCSV(brandData.brandLevel?.table_data || [], "brand_level_table.csv");
  };

  return (
    <div className="p-4 space-y-8">
      <PostAnalysisHeaderWithFilters
        title="Detailed Insights"
        filters={filters}
        setFilters={setFilters}
        baseFilterOptions={pocData.filterOptions}
        localFilterOptions={localFilterOptions}
        handleLocalFiltersChange={handleLocalFiltersChange}
      />

      {/* Chart/Table Panels (API-driven) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="min-h-[600px]">
          <Panel
            title="POC Level View"
            chartData={pocData.pocLevel?.chart_data?.x_axis?.data && pocData.pocLevel?.chart_data?.y_axis?.data ? pocData.pocLevel.chart_data.x_axis.data.map((name, i) => ({ 
              name, 
              value: pocData.pocLevel.chart_data.y_axis.data[i]
            })) : []}
            yAxis={getLabelFromValue(pocMetricAxis)}
            onYChange={displayName => setPocMetricAxis(getValueFromLabel(displayName))}
            tableData={pocData.pocLevel?.table_data || []}
            TableComponent={props => <UnifiedTable columns={POC_COLUMNS} data={props.raw_data} idKey="poc_id" onExportCSV={handleExportPocCSV} />}
            yAxisOptions={yAxisOptions}
            sortOptions={SORT_OPTIONS}
            yAxisFormatter={value => formatValue(value, { type: getMetricType(pocMetricAxis) })}
            formatValue={value => formatValue(value, { type: getMetricType(pocMetricAxis) })}
          />
        </div>
        <div className="min-h-[600px]">
          <Panel
            title="Campaign View"
            chartData={campData.campaignLevel?.chart_data?.x_axis?.data && campData.campaignLevel?.chart_data?.y_axis?.data ? campData.campaignLevel.chart_data.x_axis.data.map((name, i) => ({ 
              name, 
              value: campData.campaignLevel.chart_data.y_axis.data[i]
            })) : []}
            yAxis={getLabelFromValue(campaignMetricAxis)}
            onYChange={displayName => setCampaignMetricAxis(getValueFromLabel(displayName))}
            tableData={campData.campaignLevel?.table_data || []}
            TableComponent={props => <UnifiedTable columns={CAMPAIGN_COLUMNS} data={props.raw_data} idKey="campaign_name" onExportCSV={handleExportCampaignCSV} />}
            yAxisOptions={yAxisOptions}
            sortOptions={SORT_OPTIONS}
            yAxisFormatter={value => formatValue(value, { type: getMetricType(campaignMetricAxis) })}
            formatValue={value => formatValue(value, { type: getMetricType(campaignMetricAxis) })}
          />
        </div>
        <div className="min-h-[600px]">
          <Panel
            title="Regional View"
            chartData={regionData.regionLevel?.chart_data?.x_axis?.data && regionData.regionLevel?.chart_data?.y_axis?.data ? regionData.regionLevel.chart_data.x_axis.data.map((name, i) => ({ 
              name, 
              value: regionData.regionLevel.chart_data.y_axis.data[i]
            })) : []}
            yAxis={getLabelFromValue(regionMetricAxis)}
            onYChange={displayName => setRegionMetricAxis(getValueFromLabel(displayName))}
            tableData={regionData.regionLevel?.table_data || []}
            TableComponent={props => <UnifiedTable columns={REGION_COLUMNS} data={props.raw_data} idKey="region" onExportCSV={handleExportRegionCSV} />}
            yAxisOptions={yAxisOptions}
            sortOptions={SORT_OPTIONS}
            yAxisFormatter={value => formatValue(value, { type: getMetricType(regionMetricAxis) })}
            formatValue={value => formatValue(value, { type: getMetricType(regionMetricAxis) })}
          />
        </div>
        <div className="min-h-[600px]">
          <Panel
            title="Brand View"
            chartData={brandData.brandLevel?.chart_data?.x_axis?.data && brandData.brandLevel?.chart_data?.y_axis?.data ? brandData.brandLevel.chart_data.x_axis.data.map((name, i) => ({ 
              name, 
              value: brandData.brandLevel.chart_data.y_axis.data[i]
            })) : []}
            yAxis={getLabelFromValue(brandMetricAxis)}
            onYChange={displayName => setBrandMetricAxis(getValueFromLabel(displayName))}
            tableData={brandData.brandLevel?.table_data || []}
            TableComponent={props => <UnifiedTable columns={BRAND_COLUMNS} data={props.raw_data} idKey="brand" onExportCSV={handleExportBrandCSV} />}
            yAxisOptions={yAxisOptions}
            sortOptions={SORT_OPTIONS}
            yAxisFormatter={value => formatValue(value, { type: getMetricType(brandMetricAxis) })}
            formatValue={value => formatValue(value, { type: getMetricType(brandMetricAxis) })}
          />
        </div>
      </div>
    </div>
  )
}
